import BRAND from '../../config/branding';

// ─────────────────────────────────────────────────────────────────────────────
// Tenant Admin Tour Steps  (revised flow)
//
// Step order:
//   1.  Welcome modal (animated, minimal content)
//   2.  Home
//   3.  Users
//   4.  Settings (nav item) → then each tab: General, Integrations, Appearance, Hybrid Agents
//   5.  AIDAS Apps section header
//   6.  Data Sources
//   7.  Data Models
//   8.  Knowledge Base
//   9.  Dashboard Studio
//   10. Flows / Workflow Studio
//   11. Published Apps section header
//   12. My Workspace section header
//   13. Agentic Insights
//   14. Scheduler
//   15. Alert Rules
//   16. Credit Usage
//   17. Execution Logs
//   18. Support
//   19. Profile menu  (finish here, with restart note)
//
// ID conventions (generated by PlatformLayout RenderMenuItem):
//   Item buttons   → id="sidebar-{item.id.replace(/_/g, '-')}"
//   Section headers (explicit) → #sidebar-aidas-apps, #sidebar-published-apps, #sidebar-my-apps
//   Settings tabs  → #tab-0 … #tab-3  (General/Integrations/Appearance/Hybrid Agents)
//   Top-right avatar → #user-profile-menu
//
// scrollIntoView:true — TourEngine will call el.scrollIntoView() before spotlighting
//   (ensures collapsed/off-screen items are always visible before the tooltip appears)
// skipIfNotFound:true — silently advance if the element is absent (feature not enabled)
// ─────────────────────────────────────────────────────────────────────────────

const P = 'right-start'; // sidebar items are on the left → tooltip opens to the right

export const getTenantAdminSteps = (features = {}) => {
  const steps = [];

  // ── Step 1: Welcome (centered animated hero modal) ────────────────────────
  steps.push({
    target: null,
    isWelcome: true,
    title: 'Welcome to our Platform',
    content: "Let's take a 2-minute tour to show you where everything lives.\nYou can skip at any time, and restart from your profile menu.",
    placement: 'center',
    disableBeacon: true,
  });

  // ── Step 2: Home ──────────────────────────────────────────────────────────
  steps.push({
    target: '#sidebar-home',
    scrollIntoView: true,
    title: '🏠 Home',
    content: 'This is your home dashboard — a high-level overview of your workspace activity, recent items, and key metrics at a glance.',
    placement: P,
  });

  // ── Step 3: Users ─────────────────────────────────────────────────────────
  // Lives inside the SETTINGS accordion at the bottom of the sidebar.
  // handleTourSidebarReady already expands it. scrollIntoView ensures it's visible.
  steps.push({
    target: '#sidebar-users',
    scrollIntoView: true,
    title: '👥 Users',
    content: 'Invite team members to your workspace, assign them roles, and control who has access to which modules and data.',
    placement: P,
  });

  // ── Step 4: Settings tabs ─────────────────────────────────────────────────
  // requiresPage:'settings' tells PlatformLayout (via onStepChange) to navigate
  // to the Settings page before the TourEngine polls for #tab-0..#tab-3.
  // The first tab step covers the overall Settings page intro + General tab.
  steps.push({
    target: '#tab-0',
    requiresPage: 'settings',
    scrollIntoView: true,
    skipIfNotFound: true,
    title: '⚙️ Settings — General',
    content: 'Your Settings page lets you configure the entire workspace.\n\nThe General tab covers your organisation name, timezone, and logo — core identity settings for your workspace.',
    placement: 'bottom',
  });

  steps.push({
    target: '#tab-1',
    requiresPage: 'settings',
    scrollIntoView: true,
    skipIfNotFound: true,
    title: '🔗 Settings — Integrations',
    content: 'Connect third-party services such as email providers, webhooks, and SSO identity providers from the Integrations tab.',
    placement: 'bottom',
  });

  steps.push({
    target: '#tab-2',
    requiresPage: 'settings',
    scrollIntoView: true,
    skipIfNotFound: true,
    title: '🎨 Settings — Appearance',
    content: 'Customise your workspace theme, accent colours, and font choices so the platform matches your brand identity.',
    placement: 'bottom',
  });

  steps.push({
    target: '#tab-3',
    requiresPage: 'settings',
    scrollIntoView: true,
    skipIfNotFound: true,
    title: '🖥️ Settings — Hybrid Agents',
    content: 'Register and manage on-premise Relay Agents that securely bridge your internal data sources with the cloud platform.',
    placement: 'bottom',
  });


  // ── Step 5: AIDAS Apps section ────────────────────────────────────────────
  // requiresPage:'landing' navigates back to home after the Settings tab steps
  // so the content area no longer shows the Settings page.
  if (features?.assigned_apps !== false) {
    steps.push({
      target: '#sidebar-aidas-apps',
      requiresPage: 'landing',
      scrollIntoView: true,
      skipIfNotFound: true,
      title: `📱 ${BRAND.name.split(' ')[0]} Apps`,
      content: 'This section shows the apps your platform or partner admin has assigned to your organisation — pre-built dashboards and workflows ready to use immediately.',
      placement: P,
    });
  } else {
    // Even if no AIDAS apps, navigate back to landing before continuing
    steps.push({
      target: '#sidebar-datasources',
      requiresPage: 'landing',
      scrollIntoView: true,
      title: '🔌 Data Sources',
      content: 'Connect your databases, APIs, cloud storage, and file stores here. Every dashboard and workflow is powered by the data sources you configure.',
      placement: P,
    });
  }


  // ── Step 6: Data Sources ─────────────────────────────────────────────────
  // (only added here if AIDAS Apps was present — otherwise it was already pushed in the else above)
  if (features?.assigned_apps !== false) {
    steps.push({
      target: '#sidebar-datasources',
      scrollIntoView: true,
      title: '🔌 Data Sources',
      content: 'Connect your databases, APIs, cloud storage, and file stores here. Every dashboard and workflow is powered by the data sources you configure.',
      placement: P,
    });
  }

  // ── Step 7: Data Models ──────────────────────────────────────────────────
  if (features?.data_models !== false) {
    steps.push({
      target: '#sidebar-datamodels',
      scrollIntoView: true,
      skipIfNotFound: true,
      title: '🗄️ Data Models',
      content: 'Semantically map and structure your connected data into clean models. Better models mean more accurate, consistent reporting across all your apps and dashboards.',
      placement: P,
    });
  }

  // ── Step 8: Knowledge Base ───────────────────────────────────────────────
  if (features?.semantic_kb !== false) {
    steps.push({
      target: '#sidebar-semantic-kb',
      scrollIntoView: true,
      skipIfNotFound: true,
      title: '📚 Knowledge Base',
      content: 'Teach the AI the context of your business — define domain terms, map synonyms, and improve the precision of AI-generated insights for your industry.',
      placement: P,
    });
  }

  // ── Step 9: Dashboard Studio ─────────────────────────────────────────────
  if (features?.dashboard_studio !== false) {
    steps.push({
      target: '#sidebar-dashboards',
      scrollIntoView: true,
      skipIfNotFound: true,
      title: '📊 Dashboards',
      content: 'Build and publish interactive Business Intelligence dashboards with charts, KPI cards, date filters, and drill-through — no code required.',
      placement: P,
    });
  }

  // ── Step 10: Flows / Workflow Studio ─────────────────────────────────────
  if (features?.workflow_studio !== false) {
    steps.push({
      target: '#sidebar-flows',
      scrollIntoView: true,
      skipIfNotFound: true,
      title: '⚡ Flows',
      content: 'Design powerful automation workflows with a visual drag-and-drop editor. Connect data triggers, transformations, AI models, and actions — all without writing a line of code.',
      placement: P,
    });
  }

  // ── Step 11: Published Apps section header ────────────────────────────────
  steps.push({
    target: '#sidebar-published-apps',
    scrollIntoView: true,
    skipIfNotFound: true,
    title: '🌐 Published Apps',
    content: 'Content your team has built and published organisation-wide appears here — dashboards and workflows accessible to all permitted members of your workspace.',
    placement: P,
  });

  // ── Step 12: My Workspace section header ──────────────────────────────────
  steps.push({
    target: '#sidebar-my-apps',
    scrollIntoView: true,
    skipIfNotFound: true,
    title: '🗂️ My Workspace',
    content: 'Your personal workspace for work-in-progress items — private dashboards and draft workflows only you can see until you choose to publish them.',
    placement: P,
  });

  // ── Step 13: Agentic Insights ───────────────────────────────────────────────
  if (features?.ai_insight !== false || features?.AIINSIGHT_NLQ !== false) {
    steps.push({
      target: '#sidebar-ai-insight',
      scrollIntoView: true,
      skipIfNotFound: true,
      title: '✨ Agentic Insights',
      content: 'Ask questions about your data in plain English and get instant AI-generated answers, charts, and narrative summaries — all powered by your connected data models.',
      placement: P,
    });
  }

  // ── Step 14: Scheduler ───────────────────────────────────────────────────
  steps.push({
    target: '#sidebar-automations',
    scrollIntoView: true,
    skipIfNotFound: true,
    title: '🕐 Scheduler',
    content: 'Automate recurring data refreshes and workflow triggers. Set up daily, weekly, or custom cron-based schedules so your data is always up to date.',
    placement: P,
  });

  // ── Step 15: Alert Rules ─────────────────────────────────────────────────
  steps.push({
    target: '#sidebar-alerts',
    scrollIntoView: true,
    skipIfNotFound: true,
    title: '🔔 Alert Rules',
    content: 'Set up smart notifications for when your KPIs cross thresholds. Receive email, webhook, or in-app alerts the moment something important changes in your data.',
    placement: P,
  });

  // ── Step 16: Credit Usage ─────────────────────────────────────────────────
  // Lives inside the SETTINGS accordion. Expanded by onSidebarReady.
  steps.push({
    target: '#sidebar-credits',
    scrollIntoView: true,
    skipIfNotFound: true,
    title: '💳 Credit Usage',
    content: 'Monitor your platform API credit balance here. Credits are consumed when AI generates insights, workflows run, or data is refreshed. Keep an eye on usage to plan your team\'s activity.',
    placement: P,
  });

  // ── Step 17: Execution Logs ───────────────────────────────────────────────
  steps.push({
    target: '#sidebar-logs',
    scrollIntoView: true,
    skipIfNotFound: true,
    title: '📋 Execution Logs',
    content: 'Track the real-time status of all background jobs — data refreshes, scheduled workflows, and AI tasks. Quickly spot failures and re-trigger runs from here.',
    placement: P,
  });

  // ── Step 18: Support ──────────────────────────────────────────────────────
  steps.push({
    target: '#sidebar-support',
    scrollIntoView: true,
    skipIfNotFound: true,
    title: '🎧 Support',
    content: 'Raise a support ticket, browse the knowledge base, or chat with our team — all from one place. We\'re here whenever you need help.',
    placement: P,
  });

  // ── Step 19: Profile Menu (final step) ────────────────────────────────────
  steps.push({
    target: '#user-profile-menu',
    scrollIntoView: true,
    title: '🎉 You\'re all set!',
    content: 'That\'s the full tour! Your profile menu is where you manage your account, change your password, enable MFA, and access API keys.\n\n💡 Want to revisit this tour? Click your profile avatar → "Restart Tour" at any time.',
    placement: 'bottom-end',
  });

  return steps;
};
